import { FloatingPortal } from '@floating-ui/react';
import React, { RefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Color, Scene, Vector2, Vector3 } from 'three';

import { isDefined } from '../../../../utils/variables';
import { PickingLine } from '../../../../viewer/tools/picking-line';
import { mouseMovementTreshold } from '../../../../viewer/types';
import { useObject3d } from '../../../hooks/three/use-object-3d';
import { useFloatingPosition } from '../../../hooks/use-floating-position';
import { useMousePosition } from '../../../hooks/use-mouse-position';
import { usePointerEvent } from '../../../hooks/use-pointer-up-event';
import { Stack } from '../../stack/stack';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';
import { sfmViewerLayering } from './layering';
import { SfmViewerTool } from './sfm-viewer-tooltip';
import { CursorIntersectionResult } from './use-cursor';

type Props = {
  intersection?: CursorIntersectionResult;
  cursorPosition?: Vector3;
  webGlScene?: Scene;
  pointerRef?: RefObject<HTMLDivElement>;
  onToolChange?: (tool: SfmViewerTool) => void;
};

export const SfmViewerMeasureTool = ({
  cursorPosition,
  webGlScene,
  pointerRef,
  onToolChange,
}: Props) => {
  const { origo } = useContext(GeodataSpatialReferenceContext);
  const measureLine = useRef(new PickingLine(new Color(0, 1, 0)));
  const [distance, setDistance] = useState<number>();
  const [lastMousePosition, setLastMousePosition] = useState<Vector2>(new Vector2());
  const [isMouseInContainer, setIsMouseInContainer] = useState(false);
  const mousePosition = useMousePosition(pointerRef);
  const [measureStartPoint, setMeasureStartPoint] =
    useState<Omit<CursorIntersectionResult, 'intersection'>>();
  const { t } = useTranslation();

  const {
    ref: floatingRef,
    floatingStyles,
    floatingProps,
  } = useFloatingPosition({
    followMousePointer: true,
    middleware: 'shift',
    offset: {
      mousePointer: 10,
      viewportEdge: 5,
    },
    startPos: {
      x: mousePosition.client.x,
      y: mousePosition.client.y,
    },
    strategy: 'fixed',
  });

  useEffect(() => {
    if (!pointerRef?.current) {
      return;
    }

    const handleMouseEnter = () => {
      setIsMouseInContainer(true);
    };

    const handleMouseLeave = () => {
      setIsMouseInContainer(false);
    };

    const container = pointerRef.current;
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [pointerRef]);

  useEffect(() => {
    void mousePosition;
    // Only show measuring line when start point is set
    if (!isDefined(measureStartPoint)) {
      measureLine.current.visible = false;
      setDistance(undefined);
      return;
    }

    // Always show line when start point is set, regardless of whether we have an intersection
    measureLine.current.visible = true;

    const startPoint = measureStartPoint.localPosition;
    // Always use the cursor's current position (which updates immediately)
    const endPoint = cursorPosition?.clone() ?? startPoint;

    // Use green if we have an intersection (snapped), cyan if not
    measureLine.current.setColor(new Color(0, 1, 0));

    measureLine.current.setNonIndexedVertices([startPoint, endPoint]);
    setDistance(endPoint.distanceTo(startPoint));
  }, [measureStartPoint, cursorPosition, mousePosition]);

  useObject3d(webGlScene, measureLine.current);

  const onLeftUp = useCallback(
    (mouseEvent: MouseEvent) => {
      if (
        new Vector2(mouseEvent.clientX, mouseEvent.clientY).distanceTo(lastMousePosition) >
        mouseMovementTreshold
      ) {
        return;
      }

      // In measure mode, left click sets measurement point if it is not already set
      if (!isDefined(measureStartPoint) && isDefined(cursorPosition) && isDefined(origo)) {
        setMeasureStartPoint({
          localPosition: cursorPosition.clone(),
          mousePosition: mousePosition,
          coordinates: {
            easting: cursorPosition.x + origo.x,
            northing: cursorPosition.y + origo.y,
            height: cursorPosition.z + origo.z,
          },
        });
      }
    },
    [lastMousePosition, measureStartPoint, cursorPosition, mousePosition, origo],
  );

  const onCancelMeasure = useCallback(() => {
    // Right click in measure mode: cancel measurement or revert to select tool
    if (isDefined(measureStartPoint)) {
      setMeasureStartPoint(undefined);
    } else {
      // No active measurement, revert to select tool
      onToolChange?.('select');
    }
  }, [measureStartPoint, onToolChange]);

  const onLeftDown = useCallback((mouseEvent: MouseEvent) => {
    setLastMousePosition(new Vector2(mouseEvent.clientX, mouseEvent.clientY));
  }, []);

  usePointerEvent('pointerdown', pointerRef, 'left', onLeftDown);
  usePointerEvent('pointerup', pointerRef, 'left', onLeftUp);
  usePointerEvent('pointerup', pointerRef, 'right', onCancelMeasure);

  // Calculate coordinates and position for tooltip display - use intersection if available, otherwise use cursorPosition
  const getMeasurementData = useCallback(() => {
    // No intersection - use cursorPosition
    if (!isDefined(cursorPosition) || !isDefined(origo)) {
      return undefined;
    }

    return {
      coordinates: {
        easting: cursorPosition.x + origo.x,
        northing: cursorPosition.y + origo.y,
        height: cursorPosition.z + origo.z,
      },
      localPosition: cursorPosition,
    };
  }, [cursorPosition, origo]);

  const measurementData = getMeasurementData();
  const measurementCoordinates = measurementData?.coordinates;
  const currentLocalPosition = measurementData?.localPosition;

  // Show measurement tooltip when mouse is in container
  const isMeasurementVisible = isMouseInContainer;

  const tooltipContent =
    isMeasurementVisible && isDefined(measurementCoordinates) ? (
      <Stack spacing={0.2}>
        {isDefined(distance) && isDefined(measureStartPoint) && isDefined(currentLocalPosition) ? (
          <>
            <span>
              {t('gcpPage.measureTool.distance', { ns: 'cloudProcessing' })}: {distance.toFixed(3)}{' '}
              m
            </span>
            <span>
              {t('gcpPage.measureTool.planarDistance', { ns: 'cloudProcessing' })}:{' '}
              {currentLocalPosition
                .distanceTo(measureStartPoint.localPosition.clone().setZ(currentLocalPosition.z))
                .toFixed(3)}{' '}
              m
            </span>
            <span>
              {t('gcpPage.measureTool.heightDifference', { ns: 'cloudProcessing' })}:{' '}
              {(currentLocalPosition.z - measureStartPoint.localPosition.z).toFixed(3)} m
            </span>
            <span>N: {measurementCoordinates.northing.toFixed(3)}</span>
            <span>E: {measurementCoordinates.easting.toFixed(3)}</span>
            <span>H: {measurementCoordinates.height.toFixed(3)}</span>
          </>
        ) : (
          <>
            <span>N: {measurementCoordinates.northing.toFixed(3)}</span>
            <span>E: {measurementCoordinates.easting.toFixed(3)}</span>
            <span>H: {measurementCoordinates.height.toFixed(3)}</span>
          </>
        )}
      </Stack>
    ) : null;

  if (!tooltipContent) {
    return null;
  }

  return (
    <FloatingPortal>
      <Component ref={floatingRef} style={floatingStyles} {...floatingProps}>
        {tooltipContent}
      </Component>
    </FloatingPortal>
  );
};

const Component = styled.div`
  color: white;
  background-color: rgba(0, 0, 0, 0.72);
  border: 1px solid ${({ theme }) => theme.color.gray.darkest};
  padding: 0.5em;
  z-index: ${sfmViewerLayering.tooltip};
  pointer-events: none;
`;
